import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Users,
  Package,
  Receipt,
  CurrencyInr,
  Warning,
  Clock,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [lowStock, setLowStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, invoicesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/alerts/low-stock`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentInvoices(invoicesRes.data.slice(0, 5));
      setLowStock(alertsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (invoice) => {
    if (!invoice.due_date || invoice.status === "paid") return false;
    return new Date(invoice.due_date) < new Date();
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Revenue",
      value: `\u20b9${stats?.total_revenue?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
      icon: CurrencyInr,
      color: "bg-blue-500",
      testId: "stat-revenue",
    },
    {
      title: "Outstanding",
      value: `\u20b9${stats?.total_outstanding?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}`,
      icon: Clock,
      color: "bg-amber-600",
      testId: "stat-outstanding",
    },
    {
      title: "Overdue",
      value: stats?.overdue_invoices || 0,
      icon: Warning,
      color: "bg-red-600",
      testId: "stat-overdue",
    },
    {
      title: "Total Invoices",
      value: stats?.total_invoices || 0,
      icon: Receipt,
      color: "bg-slate-700",
      testId: "stat-invoices",
    },
  ];

  return (
    <div className="p-4 sm:p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">
          Dashboard
        </h1>
        <p className="text-slate-500 text-sm">
          Overview of your billing operations
        </p>
      </div>

      {lowStock && lowStock.count > 0 && (
        <button
          type="button"
          data-testid="dashboard-low-stock-strip"
          onClick={() => navigate("/godown")}
          className="w-full text-left mb-6 bg-amber-50 border border-amber-300 rounded-sm px-4 py-3 flex items-center gap-3 hover:bg-amber-100 transition-colors"
        >
          <Warning size={20} weight="fill" className="text-amber-600 shrink-0" />
          <span className="text-sm font-bold text-amber-900">
            Low Stock Alert: {lowStock.count} item{lowStock.count > 1 ? "s" : ""} below reorder level
          </span>
          <span className="text-xs text-amber-700 truncate">
            {[...lowStock.godown_alerts.map((a) => `${a.item_name} (${a.godown_name}: ${a.qty} ${a.unit})`),
              ...lowStock.product_alerts.map((a) => `${a.item_name} (${a.current_stock} ${a.unit})`)].slice(0, 4).join(" · ")}
          </span>
          <span className="ml-auto text-xs font-black text-amber-800 whitespace-nowrap">View in Godown →</span>
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            data-testid={stat.testId}
            className="bg-white border border-slate-200 rounded-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center`}
              >
                <stat.icon size={24} weight="duotone" className="text-white" />
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
              {stat.title}
            </p>
            <p className="text-3xl font-black tracking-tight text-slate-900">
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">
              Recent Invoices
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Latest billing activity
            </p>
          </div>
          <button
            onClick={() => navigate("/invoices/create")}
            data-testid="create-invoice-btn"
            className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200"
          >
            + New Invoice
          </button>
        </div>

        {recentInvoices.length === 0 ? (
          <div className="p-12 text-center">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/c2875c4c-3209-4e71-8b15-de610df9cec1/images/6f94e3573c58a84c73fc78db7a1660d6d91c6b0e4489f8721400bcc17382d354.png"
              alt="No invoices"
              className="w-64 h-64 mx-auto mb-6 opacity-60"
            />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              No invoices yet
            </h3>
            <p className="text-slate-500 mb-6">
              Create your first invoice to get started
            </p>
            <button
              onClick={() => navigate("/invoices/create")}
              data-testid="empty-state-create-btn"
              className="px-6 py-3 bg-blue-600 text-white rounded-sm font-semibold hover:bg-blue-700 transition-all duration-200"
            >
              Create Invoice
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto sticky-table">
            <table className="w-full">
              <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Invoice No
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Due Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {recentInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    data-testid={`invoice-row-${invoice.invoice_no}`}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                  >
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                      {invoice.invoice_no}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {invoice.customer_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {format(new Date(invoice.invoice_date), "dd MMM yyyy")}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {invoice.due_date ? (
                        <span className={isOverdue(invoice) ? "text-red-600 font-semibold" : "text-slate-600"}>
                          {format(new Date(invoice.due_date), "dd MMM yyyy")}
                          {isOverdue(invoice) && (
                            <Warning size={14} weight="fill" className="inline ml-1 text-red-500" />
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-400">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                      {"\u20b9"}{invoice.total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wider ${
                          invoice.status === "paid"
                            ? "bg-green-100 text-green-800"
                            : invoice.status === "partial"
                            ? "bg-blue-100 text-blue-800"
                            : isOverdue(invoice)
                            ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {isOverdue(invoice) && invoice.status !== "paid" ? "OVERDUE" : invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
